import React, { useState } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { shopCategoriesL1, shopCategoriesL2, shops, shopOwners } from '../../data/shopData';

const ShopHierarchy = () => {
  const [selectedCategoryL1, setSelectedCategoryL1] = useState(null);
  const [selectedCategoryL2, setSelectedCategoryL2] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('totalSales');
  const [showByOwner, setShowByOwner] = useState(false);
  
  // Filter the shop categories based on selections
  const filteredCategoriesL2 = selectedCategoryL1
    ? shopCategoriesL2.filter(cat => cat.parentId === selectedCategoryL1)
    : [];
    
  // Get filtered shops based on selection
  const getFilteredShops = () => {
    if (selectedCategoryL2) {
      return shops.filter(shop => shop.categoryL2Id === selectedCategoryL2);
    } else if (selectedCategoryL1) {
      const categoryL2Ids = filteredCategoriesL2.map(cat => cat.id);
      return shops.filter(shop => categoryL2Ids.includes(shop.categoryL2Id));
    } 
    return [];
  };
  
  // Format metric values
  const formatMetricValue = (value) => {
    switch(selectedMetric) {
      case 'totalSales':
        return `$${(value/1000000).toFixed(2)}M`;
      case 'growth':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString();
    }
  };
  
  // Prepare data for pie chart visualization
  const preparePieChartData = () => {
    // If showing by owner
    if (showByOwner) {
      if (!selectedCategoryL1) {
        // Group by owner across all categories
        const ownerData = [];
        const ownerMap = new Map();
        
        shops.forEach(shop => {
          const ownerId = shop.ownerId || 0;
          if (!ownerMap.has(ownerId)) {
            const owner = shopOwners.find(o => o.id === ownerId);
            ownerMap.set(ownerId, {
              name: owner ? owner.name : 'Independent',
              value: 0
            });
          }
          
          const ownerEntry = ownerMap.get(ownerId);
          ownerEntry.value += shop[selectedMetric];
        });
        
        return Array.from(ownerMap.values());
      } else {
        // Group by owner within selected category
        const ownerData = [];
        const ownerMap = new Map();
        
        getFilteredShops().forEach(shop => {
          const ownerId = shop.ownerId || 0;
          if (!ownerMap.has(ownerId)) {
            const owner = shopOwners.find(o => o.id === ownerId);
            ownerMap.set(ownerId, {
              name: owner ? owner.name : 'Independent',
              value: 0
            });
          }
          
          const ownerEntry = ownerMap.get(ownerId);
          ownerEntry.value += shop[selectedMetric];
        });
        
        return Array.from(ownerMap.values());
      }
    } 
    // Showing by category hierarchy
    else {
      if (!selectedCategoryL1) {
        // Show all level 1 categories
        return shopCategoriesL1.map(cat => ({
          name: cat.name,
          value: cat[selectedMetric]
        }));
      } else if (selectedCategoryL1 && !selectedCategoryL2) {
        // Show level 2 categories within selected level 1
        return filteredCategoriesL2.map(cat => ({
          name: cat.name,
          value: cat[selectedMetric]
        }));
      } else if (selectedCategoryL1 && selectedCategoryL2) {
        // Show shops within selected level 2 category
        return getFilteredShops().map(shop => ({
          name: shop.name,
          value: shop[selectedMetric]
        }));
      }
    }
    
    return [];
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1', '#A4DE6C', '#D0ED57', '#FFAA33'];
  
  // Click handlers for navigation
  const handleCategoryL1Click = (categoryId) => {
    setSelectedCategoryL1(categoryId);
    setSelectedCategoryL2(null);
  };
  
  const handleCategoryL2Click = (categoryId) => {
    setSelectedCategoryL2(categoryId);
  };
  
  const handleBackClick = () => {
    if (selectedCategoryL2) {
      setSelectedCategoryL2(null);
    } else if (selectedCategoryL1) {
      setSelectedCategoryL1(null);
    }
  };
  
  // Get shop owner information for a given shop
  const getShopOwner = (ownerId) => {
    if (!ownerId) return 'Independent';
    const owner = shopOwners.find(o => o.id === ownerId);
    return owner ? owner.name : 'Unknown';
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Shop Hierarchy Analysis</h1>
        <p className="text-gray-600 mb-4">
          Explore the hierarchical structure of retail shops. Drill down from top-level categories to specific shops and analyze performance metrics.
        </p>
        
        {/* Navigation breadcrumbs */}
        <div className="flex items-center text-sm mb-4">
          <button 
            onClick={() => setSelectedCategoryL1(null)}
            className={`font-medium ${!selectedCategoryL1 ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            All Shop Categories
          </button>
          
          {selectedCategoryL1 && (
            <>
              <svg className="h-4 w-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <button 
                onClick={() => setSelectedCategoryL2(null)}
                className={`font-medium ${!selectedCategoryL2 ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {shopCategoriesL1.find(cat => cat.id === selectedCategoryL1)?.name || 'Category'}
              </button>
            </>
          )}
          
          {selectedCategoryL2 && (
            <>
              <svg className="h-4 w-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="font-medium text-blue-600">
                {shopCategoriesL2.find(cat => cat.id === selectedCategoryL2)?.name || 'Subcategory'}
              </span>
            </>
          )}
        </div>
        
        {/* Control panel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Metric</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="dropdown-selector"
            >
              <option value="totalSales">Total Sales (USD)</option>
              <option value="growth">Growth Rate (%)</option>
              <option value="productCount">Product Count</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Grouping</label>
            <div className="flex">
              <button
                onClick={() => setShowByOwner(false)}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  !showByOwner 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                By Category
              </button>
              <button
                onClick={() => setShowByOwner(true)}
                className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                  showByOwner 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                By Owner
              </button>
            </div>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={handleBackClick}
              disabled={!selectedCategoryL1}
              className={`px-4 py-2 rounded-md font-medium text-sm ${
                !selectedCategoryL1 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              ← Back Up One Level
            </button>
          </div>
        </div>
      </div>
      
      {/* Hierarchy Visualization */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {showByOwner ? 'Shop Owners Hierarchy' : 'Shop Category Hierarchy'}
        </h2>
        <div style={{ height: 500 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={preparePieChartData()}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                outerRadius={180}
                fill="#8884d8"
                dataKey="value"
              >
                {preparePieChartData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => formatMetricValue(value)}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Performance Chart */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {selectedCategoryL2 
            ? 'Shop Performance' 
            : selectedCategoryL1 
              ? 'Subcategory Performance' 
              : 'Category Performance'}
        </h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={
                selectedCategoryL2 
                  ? getFilteredShops()
                  : selectedCategoryL1 
                    ? filteredCategoriesL2
                    : shopCategoriesL1
              }
              layout="vertical"
              margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                type="category" 
                dataKey="name" 
                tick={{ fontSize: 12 }}
              />
              <Tooltip formatter={(value) => formatMetricValue(value)} />
              <Legend />
              <Bar 
                dataKey={selectedMetric} 
                fill="#00C49F" 
                name={selectedMetric === 'totalSales' 
                      ? 'Total Sales' 
                      : selectedMetric === 'growth'
                      ? 'Growth Rate'
                      : 'Product Count'} 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Data table for current level */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {selectedCategoryL2 
            ? 'Shops' 
            : selectedCategoryL1 
              ? 'Subcategories' 
              : 'Top-Level Categories'}
        </h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Total Sales</th>
                <th>Growth</th>
                <th>Product Count</th>
                {selectedCategoryL2 && <th>Owner</th>}
                {selectedCategoryL2 && <th>Location Type</th>}
              </tr>
            </thead>
            <tbody>
              {(selectedCategoryL2 
                ? getFilteredShops().sort((a, b) => b[selectedMetric] - a[selectedMetric])
                : selectedCategoryL1 
                  ? filteredCategoriesL2.sort((a, b) => b[selectedMetric] - a[selectedMetric])
                  : shopCategoriesL1.sort((a, b) => b[selectedMetric] - a[selectedMetric])
              ).map(item => (
                <tr 
                  key={item.id} 
                  onClick={() => {
                    if (!selectedCategoryL1) {
                      handleCategoryL1Click(item.id);
                    } else if (!selectedCategoryL2) {
                      handleCategoryL2Click(item.id);
                    }
                    // No action for shops
                  }}
                  className={(!selectedCategoryL2) ? 'cursor-pointer hover:bg-blue-50' : ''}
                >
                  <td>{item.name}</td>
                  <td>${(item.totalSales / 1000000).toFixed(2)}M</td>
                  <td className={item.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                    {item.growth >= 0 ? '↑' : '↓'} {Math.abs(item.growth).toFixed(1)}%
                  </td>
                  <td>{item.productCount?.toLocaleString()}</td>
                  {selectedCategoryL2 && <td>{getShopOwner(item.ownerId)}</td>}
                  {selectedCategoryL2 && (
                    <td>
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full 
                        ${item.locationType === 'physical' ? 'bg-blue-100 text-blue-800' : 
                          item.locationType === 'online' ? 'bg-green-100 text-green-800' : 
                          'bg-yellow-100 text-yellow-800'}`}
                      >
                        {item.locationType}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ShopHierarchy;